// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — the whole game, as a pure fold. Solo, peer-to-peer, the bot and the balance sim all
// run THIS file; there is no second implementation anywhere to drift from it.
//
// THERE IS NO HIDDEN INFORMATION IN THIS GAME. Every demesne, every reveal and every claim is public
// and always has been, which is what lets the netcode be hostless deterministic lockstep instead of
// a host-authoritative star: no peer needs to be trusted with anything, because
//
//     derive(seed, mode, players, log) → State
//
// is a pure function every peer can evaluate. Host transfer is therefore a no-op after the first
// move; onHostChange repaints and never navigates.
//
// ONE ATOMIC MOVE PER TURN — CLAIM AND PLACE TOGETHER. The physical genre claims now and places next
// round, and reproducing that would mean a peer can hold an unplaced doublet, which is a whole class
// of state to synchronise and to recover after a disconnect. Collapsing it removes that class
// outright and plays better on a phone, because your two decisions are adjacent in time.
//
// THE KEY IS IN THE HIGH BITS, so sorting the log IS canonical order:
//
//     key  = (round << 3) | slot                       // 8 bits, round 1..16, slot 0..7
//     body = pick | cell<<7 | rot<<13 | discard<<15    // 16 bits
//     move = key<<17 | body                            // 25 bits, one int
//
// Sorting by the whole word therefore sorts by round, then by slot, which is exactly play order —
// which is why holt's burn had to become SLOT 0 of the round rather than an extra field on the last
// claim or a slot after it. A burn that sorted last would be folded last, and it has to happen first.
//
// `pick` is the doublet's printed NUMBER, never an index into the displayed row. An index is only
// meaningful against a particular reveal, and the moment two peers can disagree about what the
// reveal is, an index names a different doublet on each of them and the log replays into two
// different games. The number is self-describing and cannot do that.

import { makeRng } from '@ben-gy/game-engine/rng';
import { DECK, DECK_SIZE, doubletOf, stratifiedPool, type Doublet } from './deck';
import {
  burnsIn,
  claimsPer,
  claimsPerRound,
  poolSize,
  revealSize,
  roundsIn,
  slotsInRound,
  type Mode,
  modeOf,
} from './modes';
import { EMPTY, MOTTE, scoreDemesne, winnersOf, type Tally } from './score';

export { EMPTY, MOTTE };

/**
 * Bumped whenever the meaning of a move word or the rules change. A peer announcing a different
 * revision is on a stale cached build, which is a completely different fault from a desync and has
 * a completely different fix ("reload"), so it is reported as itself.
 */
export const RULES_REV = 1;

// ── the move word ───────────────────────────────────────────────────────────────
export const moveKey = (round: number, slot: number): number => ((round & 31) << 3) | (slot & 7);
export const keyOf = (m: number): number => (m >>> 17) & 0xff;
export const roundOf = (m: number): number => (keyOf(m) >>> 3) & 31;
export const slotOf = (m: number): number => keyOf(m) & 7;
export const pickOf = (m: number): number => m & 0x7f;
export const cellOf = (m: number): number => (m >>> 7) & 0x3f;
export const rotOf = (m: number): number => (m >>> 13) & 3;
export const isDiscard = (m: number): boolean => ((m >>> 15) & 1) === 1;

export function encodeMove(o: {
  round: number;
  slot: number;
  pick: number;
  cell?: number;
  rot?: number;
  discard?: boolean;
}): number {
  return (
    (moveKey(o.round, o.slot) << 17) |
    (o.pick & 0x7f) |
    ((o.cell ?? 0) & 0x3f) * 128 |
    ((o.rot ?? 0) & 3) * 8192 |
    (o.discard ? 32768 : 0)
  );
}

/** The four orientations, as the offset from the anchor cell to the second cell. */
export const ROTS = 4;

// ── events, for the juice layer and the mechanism audit ─────────────────────────
export type EventKind = 'claim' | 'place' | 'discard' | 'merge' | 'burn' | 'round' | 'over';

/** `claimedBy` marker for a doublet holt struck out of the match. */
export const BURNED = -2;

export interface RuleEvent {
  kind: EventKind;
  seat?: number;
  /** Doublet number. */
  n?: number;
  /** Cells touched. */
  cells?: number[];
  /** Coronets landed by a placement. */
  coronets?: number;
  round?: number;
  slot?: number;
}

// ── state ───────────────────────────────────────────────────────────────────────
export interface Demesne {
  terr: Int8Array;
  cor: Int8Array;
}

export interface State {
  seed: number;
  modeId: string;
  mode: Mode;
  players: number;
  side: number;
  /** 1-based; `rounds + 1` once the match is over. */
  round: number;
  /** Slot within the round, 0-based. */
  slot: number;
  rounds: number;
  /** Seat holding each slot of the current round. */
  order: number[];
  /** The face-up row, always sorted ascending by number. */
  reveal: number[];
  /** Seat that claimed reveal[i], -1 if it is still open, BURNED if holt struck it out. */
  claimedBy: number[];
  /** holt: the leftover carried into the next reveal, or 0. */
  carried: number;
  /** Pool doublets not yet revealed, in draw order. */
  pool: number[];
  /** Highest number allowed in round 1's reveal — see drawReveal. */
  lowCut: number;
  boards: Demesne[];
  discards: number[];
  placed: number[];
  /** Every doublet number each seat has claimed, in claim order. */
  taken: number[][];
  /** Doublet numbers burned out of the match (holt), for the results screen. */
  burned: number[];
  over: boolean;
  log: number[];
}

const key = (s: State): number => moveKey(s.round, s.slot);

/** The one place a per-round random stream is made. Never a single running stream: a per-round seed
 * means a round's draw depends only on (seed, round, what is left), which is what keeps derive
 * order-independent once the log is sorted. */
const roundRng = (seed: number, round: number) => makeRng((seed + round * 2654435761) >>> 0);

export function createState(seed: number, modeId: string, players: number): State {
  const mode = modeOf(modeId);
  const side = mode.side;
  const n = side * side;
  const centre = ((side - 1) / 2) * side + (side - 1) / 2;
  const boards: Demesne[] = [];
  for (let p = 0; p < players; p++) {
    const terr = new Int8Array(n).fill(EMPTY);
    const cor = new Int8Array(n);
    terr[centre] = MOTTE;
    boards.push({ terr, cor });
  }

  const pool = stratifiedPool(seed, poolSize(mode, players));
  const sortedPool = pool.slice().sort((a, b) => a - b);
  // Round 1's reveal is drawn only from the lower 60% of the pool. Slot 1 has paid no tempo — it is
  // the one claim in the match that is free — so it must not be able to buy a three-coronet Coomb.
  const lowCut = sortedPool[Math.max(0, Math.ceil(sortedPool.length * 0.6) - 1)];

  const s: State = {
    seed,
    modeId: mode.id,
    mode,
    players,
    side,
    round: 1,
    slot: 0,
    rounds: roundsIn(mode, players),
    order: [],
    reveal: [],
    claimedBy: [],
    carried: 0,
    pool,
    lowCut,
    boards,
    discards: new Array(players).fill(0),
    placed: new Array(players).fill(0),
    taken: Array.from({ length: players }, () => [] as number[]),
    burned: [],
    over: false,
    log: [],
  };

  s.order = firstOrder(s);
  openRound(s, null);
  return s;
}

/**
 * Round 1's pick order: a seeded shuffle of the seats, snaked when each player claims twice.
 *
 * The snake matters. A flat repeat (A,B,B,A vs A,A,B,B) is not a cosmetic choice at 2P — with
 * A,A,B,B the first player takes the two best doublets every single round and the game is over
 * before it starts.
 */
function firstOrder(s: State): number[] {
  const rng = roundRng(s.seed, 0);
  const seats: number[] = [];
  for (let p = 0; p < s.players; p++) seats.push(p);
  for (let i = seats.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = seats[i];
    seats[i] = seats[j];
    seats[j] = t;
  }
  const k = claimsPer(s.players);
  if (k === 1) return seats;
  const out = seats.slice();
  for (let pass = 1; pass < k; pass++) {
    const leg = seats.slice();
    if (pass % 2 === 1) leg.reverse();
    out.push(...leg);
  }
  return out;
}

/**
 * Draw the face-up row for the current round.
 *
 * STRATIFIED, and this is the difference between a draft and a formality. Left uniform, roughly a
 * third of rounds come up as four indistinguishable middling doublets and the "choice" is nominal;
 * forcing one from the top tertile of what is left and one from the bottom means every single round
 * contains a real trade — a fat piece with a real tempo bill and a junk piece with a real rebate.
 */
function drawReveal(s: State, need: number): number[] {
  const rng = roundRng(s.seed, s.round);
  const eligible = s.round === 1 ? s.pool.filter((n) => n <= s.lowCut) : s.pool.slice();
  const cands = eligible.length >= need ? eligible : s.pool.slice();
  cands.sort((a, b) => a - b);

  const out: number[] = [];
  const take = (n: number): void => {
    out.push(n);
    const i = cands.indexOf(n);
    if (i >= 0) cands.splice(i, 1);
  };

  const tertile = Math.floor(cands.length / 3);
  if (need >= 3 && tertile >= 1) {
    take(cands[cands.length - 1 - Math.floor(rng() * tertile)]);
    take(cands[Math.floor(rng() * tertile)]);
  }
  while (out.length < need && cands.length > 0) take(cands[Math.floor(rng() * cands.length)]);

  for (const n of out) {
    const i = s.pool.indexOf(n);
    if (i >= 0) s.pool.splice(i, 1);
  }
  return out;
}

function openRound(s: State, events: RuleEvent[] | null): void {
  const size = revealSize(s.mode, s.players, s.round);
  const need = size - (s.carried > 0 ? 1 : 0);
  const drawn = drawReveal(s, need);
  const row = s.carried > 0 ? [s.carried, ...drawn] : drawn;
  s.carried = 0;
  row.sort((a, b) => a - b);
  s.reveal = row;
  s.claimedBy = new Array(row.length).fill(-1);
  events?.push({ kind: 'round', round: s.round });
}

// ── geometry ────────────────────────────────────────────────────────────────────
/** The cell a doublet's second half lands on at rotation `rot`, or -1 if it falls off the frame. */
export function mate(side: number, cell: number, rot: number): number {
  const row = (cell / side) | 0;
  const col = cell % side;
  if (rot === 0) return col + 1 < side ? cell + 1 : -1;
  if (rot === 1) return row + 1 < side ? cell + side : -1;
  if (rot === 2) return col - 1 >= 0 ? cell - 1 : -1;
  return row - 1 >= 0 ? cell - side : -1;
}

/** Does an orthogonal neighbour of `cell` carry terrain `t`, or the Motte (which is wild)? */
function touches(d: Demesne, side: number, cell: number, t: number): boolean {
  const row = (cell / side) | 0;
  const col = cell % side;
  if (col > 0 && (d.terr[cell - 1] === t || d.terr[cell - 1] === MOTTE)) return true;
  if (col < side - 1 && (d.terr[cell + 1] === t || d.terr[cell + 1] === MOTTE)) return true;
  if (row > 0 && (d.terr[cell - side] === t || d.terr[cell - side] === MOTTE)) return true;
  if (row < side - 1 && (d.terr[cell + side] === t || d.terr[cell + side] === MOTTE)) return true;
  return false;
}

/**
 * Is this doublet legal at (cell, rot) on this demesne?
 *
 * AT LEAST ONE of the two halves must touch its own terrain (or the Motte). Requiring BOTH is the
 * obvious reading and it is unplayable: on a 5×5 with an exact fill it pushes the discard rate past
 * anything a player would sit through, and turns the last four rounds into forced forfeits.
 */
export function legalAt(d: Demesne, mode: Mode, side: number, doub: Doublet, cell: number, rot: number): boolean {
  if (cell < 0 || cell >= side * side) return false;
  const b = mate(side, cell, rot);
  if (b < 0) return false;
  if (d.terr[cell] !== EMPTY || d.terr[b] !== EMPTY) return false;
  // shire: a doublet with the same land on both halves founds a colony anywhere, touching nothing.
  if (mode.colony && doub.same) return true;
  return touches(d, side, cell, doub.a.terrain) || touches(d, side, b, doub.b.terrain);
}

/** Every legal (cell, rot) for this doublet, in a FIXED enumeration order — cell ascending, then
 * rotation — because the bot breaks eval ties on this index and a different order is a live desync. */
export function legalPlacements(d: Demesne, mode: Mode, side: number, doub: Doublet): number[] {
  const out: number[] = [];
  const n = side * side;
  for (let cell = 0; cell < n; cell++) {
    if (d.terr[cell] !== EMPTY) continue;
    for (let rot = 0; rot < ROTS; rot++) if (legalAt(d, mode, side, doub, cell, rot)) out.push(cell * ROTS + rot);
  }
  return out;
}

export const hasPlacement = (d: Demesne, mode: Mode, side: number, doub: Doublet): boolean =>
  legalPlacements(d, mode, side, doub).length > 0;

// ── the fold ────────────────────────────────────────────────────────────────────
/** Is the current turn holt's opening burn rather than a claim? */
export const isBurnTurn = (s: State): boolean => !s.over && burnsIn(s.mode, s.players, s.round) && s.slot === 0;

/** How many claims have already happened this round. */
const claimIndex = (s: State): number => s.slot - (burnsIn(s.mode, s.players, s.round) ? 1 : 0);

/**
 * Whose turn it is.
 *
 * On holt's burn turn it is the seat holding the LAST claim of the round — that is the whole design:
 * you are about to pick from whatever everyone else has left you, and in exchange you get to strike
 * one doublet out of the row before anybody touches it.
 */
export function seatToPlay(s: State): number {
  if (s.over) return -1;
  if (isBurnTurn(s)) return s.order[claimsPerRound(s.players) - 1] ?? -1;
  return s.order[claimIndex(s)] ?? -1;
}

/** Reveal entries still claimable — neither taken nor burned. */
export function openPicks(s: State): number[] {
  const out: number[] = [];
  for (let i = 0; i < s.reveal.length; i++) if (s.claimedBy[i] === -1) out.push(i);
  return out;
}

/**
 * What slot would claiming doublet `n` buy me next round, given what has already been claimed?
 *
 * This is the number the player has to see. The order cost is the entire mechanic and on a phone it
 * is otherwise an inference nobody makes — you tap the top row twelve times and never learn that
 * the fat doublet cost you the initiative. Returns a 1-based slot, or 0 in the final round where
 * there is no next round to buy.
 */
export function slotRange(s: State, n: number): { lo: number; hi: number; of: number } | null {
  const of = claimsPerRound(s.players);
  if (s.round >= s.rounds) return null;
  let lower = 0;
  let lowerOpen = 0;
  for (let i = 0; i < s.reveal.length; i++) {
    if (s.reveal[i] >= n) continue;
    if (s.claimedBy[i] >= 0) lower++;
    else if (s.claimedBy[i] === -1) lowerOpen++;
  }
  // Claims already made this round. On holt's burn turn the claim index is −1 — nobody has claimed
  // yet — so the clamp keeps `after` honest about how many claims are still to come.
  const made = Math.max(0, claimIndex(s));
  const after = Math.max(0, of - made - 1);
  const lo = lower + 1;
  // Everyone still to claim this round might take a number below mine, or might not — so the slot
  // this buys is a RANGE, and saying so is more honest than a single number that is often wrong.
  //
  // THE OUTER Math.min IS WHAT STOPS THE LIE, and it is worth naming which line does the work: the
  // shipped row once offered "1st–5th of 4" on a burn turn, because five doublets sat below the one
  // being priced and nothing capped the top of the range at the number of claims that exist. The
  // clamp above is belt and braces; this is the invariant.
  return { lo, hi: Math.min(of, lo + Math.min(after, lowerOpen)), of };
}

/** Apply one move. Returns false — changing nothing — if it is not legal from here. */
export function applyMove(s: State, move: number, events: RuleEvent[] | null = null): boolean {
  if (s.over) return false;
  if (keyOf(move) !== key(s)) return false;
  const seat = seatToPlay(s);
  if (seat < 0) return false;

  const n = pickOf(move);
  const idx = s.reveal.indexOf(n);
  if (idx < 0 || s.claimedBy[idx] !== -1) return false;
  const doub = doubletOf(n);
  if (!doub) return false;

  if (isBurnTurn(s)) {
    // A burn takes a doublet out of the match entirely. No board, no claim, no placement.
    s.claimedBy[idx] = BURNED;
    s.burned.push(n);
    events?.push({ kind: 'burn', seat, n, round: s.round, slot: s.slot });
    s.log.push(move);
    advance(s, events);
    return true;
  }

  const d = s.boards[seat];
  const discard = isDiscard(move);
  const cell = cellOf(move);
  const rot = rotOf(move);

  if (discard) {
    // A voluntary discard is illegal. Two cells thrown away is a four-point Wholefold hit plus the
    // region you never built, and letting a player choose that would make the graded bonus a lie.
    if (hasPlacement(d, s.mode, s.side, doub)) return false;
  } else if (!legalAt(d, s.mode, s.side, doub, cell, rot)) {
    return false;
  }

  s.claimedBy[idx] = seat;
  s.taken[seat].push(n);
  events?.push({ kind: 'claim', seat, n, round: s.round, slot: s.slot });

  if (discard) {
    s.discards[seat]++;
    events?.push({ kind: 'discard', seat, n });
  } else {
    const b = mate(s.side, cell, rot);
    const before = neighbourRegions(d, s.side, cell, b, doub);
    d.terr[cell] = doub.a.terrain;
    d.cor[cell] = doub.a.coronets;
    d.terr[b] = doub.b.terrain;
    d.cor[b] = doub.b.coronets;
    s.placed[seat]++;
    events?.push({ kind: 'place', seat, n, cells: [cell, b], coronets: doub.coronets });
    if (before > 0) events?.push({ kind: 'merge', seat, cells: [cell, b] });
  }

  s.log.push(move);
  advance(s, events);
  return true;
}

/** How many already-placed same-terrain neighbours this placement joins — purely for the juice. */
function neighbourRegions(d: Demesne, side: number, cell: number, b: number, doub: Doublet): number {
  let hits = 0;
  const scan = (c: number, t: number): void => {
    const row = (c / side) | 0;
    const col = c % side;
    if (col > 0 && d.terr[c - 1] === t) hits++;
    if (col < side - 1 && d.terr[c + 1] === t) hits++;
    if (row > 0 && d.terr[c - side] === t) hits++;
    if (row < side - 1 && d.terr[c + side] === t) hits++;
  };
  scan(cell, doub.a.terrain);
  scan(b, doub.b.terrain);
  return hits;
}

function advance(s: State, events: RuleEvent[] | null): void {
  s.slot++;
  if (s.slot < slotsInRound(s.mode, s.players, s.round)) return;

  // holt: whatever survived the burn and the claims carries into the next row at its own number, so
  // a doublet nobody could use this round is still on the table when somebody can.
  if (s.mode.burn) {
    const left = openPicks(s)
      .map((i) => s.reveal[i])
      .sort((a, b) => a - b);
    s.carried = left.length > 0 ? left[0] : 0;
  }

  // Next round's slot i belongs to whoever claimed the i-th LOWEST number this round. Numbers are
  // unique, so there is no tie to break and nothing that two peers could resolve differently.
  const claims: Array<{ n: number; seat: number }> = [];
  for (let i = 0; i < s.reveal.length; i++) if (s.claimedBy[i] >= 0) claims.push({ n: s.reveal[i], seat: s.claimedBy[i] });
  claims.sort((a, b) => a.n - b.n);
  s.order = claims.map((c) => c.seat);

  s.round++;
  s.slot = 0;
  if (s.round > s.rounds) {
    s.over = true;
    s.reveal = [];
    s.claimedBy = [];
    events?.push({ kind: 'over' });
    return;
  }
  openRound(s, events);
}

/** Sort a log into canonical order and drop duplicate keys, smaller payload winning. */
export function canonicalLog(log: readonly number[]): number[] {
  const byKey = new Map<number, number>();
  for (const m of log) {
    const k = keyOf(m);
    const cur = byKey.get(k);
    // A key collision only happens when a disconnected seat is filled by more than one peer in the
    // same instant. Both are legal; the smaller payload is chosen so every peer picks the SAME one.
    if (cur === undefined || m < cur) byKey.set(k, m);
  }
  return [...byKey.values()].sort((a, b) => a - b);
}

/**
 * The whole game from its inputs. Sorting first is what makes this order-independent, which is the
 * property the whole-log wire protocol rests on: peers may see moves in any order, more than once,
 * or in one lump, and still land on byte-identical state.
 */
export function derive(seed: number, modeId: string, players: number, log: readonly number[]): State {
  const s = createState(seed, modeId, players);
  for (const m of canonicalLog(log)) if (!applyMove(s, m)) break;
  return s;
}

// ── results ─────────────────────────────────────────────────────────────────────
export const tallyFor = (s: State, seat: number): Tally =>
  scoreDemesne(s.boards[seat].terr, s.boards[seat].cor, s.side, {
    wholefold: s.mode.wholefold,
    holdfast: s.mode.holdfast,
  });

export function tallies(s: State): Tally[] {
  const out: Tally[] = [];
  for (let p = 0; p < s.players; p++) out.push(tallyFor(s, p));
  return out;
}

export const winners = (s: State): number[] => winnersOf(tallies(s));

/**
 * Empty cells that can never be filled, as a set of cell indices.
 *
 * A 5×5 minus its centre is exactly 12 dark and 12 light, and a 7×7 minus its centre is 24 and 24,
 * so a perfect fill is ALWAYS geometrically attainable — and any empty component whose two colours
 * are unequal can never be filled, because every doublet covers exactly one of each. Hatching those
 * live is what turns the last four rounds from guesswork into a visible puzzle, and the bot scores
 * the same quantity as parity debt.
 */
export function spoiledCells(d: Demesne, side: number): Set<number> {
  const n = side * side;
  const seen = new Uint8Array(n);
  const out = new Set<number>();
  const stack: number[] = [];
  for (let i = 0; i < n; i++) {
    if (seen[i] || d.terr[i] !== EMPTY) continue;
    const comp: number[] = [];
    let parity = 0;
    seen[i] = 1;
    stack.length = 0;
    stack.push(i);
    while (stack.length > 0) {
      const c = stack.pop() as number;
      comp.push(c);
      parity += (((c / side) | 0) + (c % side)) % 2 === 0 ? 1 : -1;
      const row = (c / side) | 0;
      const col = c % side;
      const push = (j: number): void => {
        if (!seen[j] && d.terr[j] === EMPTY) {
          seen[j] = 1;
          stack.push(j);
        }
      };
      if (col > 0) push(c - 1);
      if (col < side - 1) push(c + 1);
      if (row > 0) push(c - side);
      if (row < side - 1) push(c + side);
    }
    if (parity !== 0) for (const c of comp) out.add(c);
  }
  return out;
}

/** Σ |dark − light| over the empty components — the bot's parity debt, and 0 iff a fill is possible. */
export function parityDebt(d: Demesne, side: number): number {
  const n = side * side;
  const seen = new Uint8Array(n);
  const stack: number[] = [];
  let debt = 0;
  for (let i = 0; i < n; i++) {
    if (seen[i] || d.terr[i] !== EMPTY) continue;
    let parity = 0;
    seen[i] = 1;
    stack.length = 0;
    stack.push(i);
    while (stack.length > 0) {
      const c = stack.pop() as number;
      parity += (((c / side) | 0) + (c % side)) % 2 === 0 ? 1 : -1;
      const row = (c / side) | 0;
      const col = c % side;
      const push = (j: number): void => {
        if (!seen[j] && d.terr[j] === EMPTY) {
          seen[j] = 1;
          stack.push(j);
        }
      };
      if (col > 0) push(c - 1);
      if (col < side - 1) push(c + 1);
      if (row > 0) push(c - side);
      if (row < side - 1) push(c + side);
    }
    debt += Math.abs(parity);
  }
  return debt;
}

/** FNV-1a over the log and every board — enough to catch two peers running different rules. */
export function digest(s: State): number {
  let h = 0x811c9dc5;
  const mix = (v: number): void => {
    h ^= v & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
    h ^= (v >>> 8) & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
  };
  mix(RULES_REV);
  mix(s.round);
  mix(s.slot);
  for (const m of s.log) {
    mix(m & 0xffff);
    mix(m >>> 16);
  }
  for (const b of s.boards) {
    for (let i = 0; i < b.terr.length; i++) mix((b.terr[i] + 3) * 8 + b.cor[i]);
  }
  return h >>> 0;
}

/** Deck sanity, exported so the census test and the About panel agree on one source. */
export const DECK_STATS = {
  doublets: DECK_SIZE,
  cells: DECK_SIZE * 2,
  coronets: DECK.reduce((a, d) => a + d.coronets, 0),
  same: DECK.filter((d) => d.same).length,
};
